import { useState } from 'react';
import { api } from '../lib/api.js';

// ─── Change Password Modal ────────────────────────────────────────────────────
// Used in two places:
//   1. Forced-change overlay after login when user.password_change_required is
//      true (newly created accounts must replace the generated temp password
//      before they can use the app). `forced` prop disables Cancel.
//   2. Self-serve from the EmployeeProfileView for any user changing their
//      own password later.
export function ChangePasswordModal({ user, forced, onClose, onChanged }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (next.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setError('New passwords do not match.'); return; }
    if (next === current) { setError('New password must differ from current.'); return; }
    setSubmitting(true);
    try {
      await api('POST', '/api/auth/change-password',
        { current_password: current, new_password: next },
        { 'x-user-id': String(user.id) });
      setSuccess(true);
      onChanged && onChanged();
    } catch (err) {
      setError(err.message || 'Could not change password.');
      setSubmitting(false);
    }
  }

  return (
    <div className={`fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 ${forced ? '' : ''}`}
         onClick={forced ? undefined : onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col fade-in shadow-2xl"
           style={{ maxHeight: '85vh', height: 'auto' }}
           onClick={e => e.stopPropagation()}>
        <div className="bg-ink rounded-t-2xl p-5 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-gold text-xs uppercase tracking-widest mb-0.5">Account</p>
            <h3 className="text-cream font-bold text-base">{forced ? 'Set Your Password' : 'Change Password'}</h3>
          </div>
          {!forced && (
            <button onClick={onClose} className="text-cream/50 hover:text-cream text-3xl leading-none ml-4">×</button>
          )}
        </div>
        {success ? (
          <div className="p-6 text-center" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <div className="text-5xl mb-3">✅</div>
            <p className="font-bold text-lg text-ink mb-1">Password updated</p>
            <p className="text-sm text-gray-500 mb-6">You're all set.</p>
            <button onClick={onClose} className="w-full py-3 bg-crimson text-white font-semibold rounded-xl hover:bg-crimson-dark">Continue</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-3" style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', flex: 1, minHeight: 0 }}>
            {/* Password managers (iCloud Keychain, Google) save or update a
                password only when they can tell whose it is: the username
                field plus current/new-password hints. Without them a new
                staff member's phone kept the default password. sr-only, not
                hidden — a display:none field is ignored by some managers. */}
            <input type="text" name="username" autoComplete="username" value={user?.email || ''} readOnly tabIndex={-1} aria-hidden="true" className="sr-only" />
            {forced && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                You're using the default password. Please set a new one before continuing.
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Current password</label>
              <input type="password" name="current-password" autoComplete="current-password" value={current} onChange={e => setCurrent(e.target.value)} required autoFocus
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:border-crimson" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">New password (min 8 chars)</label>
              <input type="password" name="new-password" autoComplete="new-password" value={next} onChange={e => setNext(e.target.value)} required minLength={8}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:border-crimson" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Confirm new password</label>
              <input type="password" name="confirm-password" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none focus:border-crimson" />
            </div>
            {error && <div className="text-xs text-red-600 font-medium">{error}</div>}
            <div className="flex gap-3 pt-1">
              {!forced && (
                <button type="button" onClick={onClose}
                  className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50">Cancel</button>
              )}
              <button type="submit" disabled={submitting}
                className="flex-1 py-3 rounded-xl bg-crimson text-white font-semibold text-sm hover:bg-crimson-dark disabled:opacity-60">
                {submitting ? 'Saving…' : 'Update password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
